import React, { useState, useEffect } from 'react';
import { LeagueContext } from './LeagueContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore } from '../services/firebase';
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { cacheService } from '../services/cacheService';
import { leagueDataPreloader } from '../services/leagueDataPreloader';

export function LeagueProvider({ children }) {
  const [user] = useAuthState(auth);
  const [currentLeague, setCurrentLeague] = useState(null);
  const [userLeagues, setUserLeagues] = useState([]);
  const [leagueMembers, setLeagueMembers] = useState([]);
  const [isLeagueAdmin, setIsLeagueAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leagueDataVersion, setLeagueDataVersion] = useState(0);

  // Load user's leagues and current league on login
  useEffect(() => {
    if (!user) {
      setCurrentLeague(null);
      setUserLeagues([]);
      setLeagueMembers([]);
      setIsLeagueAdmin(false);
      // Don't set loading to false when user is null during auth restoration
      // Keep loading true until we have a confirmed user state
      return;
    }

    loadUserLeagues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // loadUserLeagues is stable, no dependency needed

  // Load current league data when currentLeague changes
  useEffect(() => {
    if (currentLeague) {
      loadLeagueMembers();
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeague, user]); // Functions are stable, no dependencies needed

  const loadUserLeagues = async () => {
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      
      // Check cache first
      const cachedUserData = cacheService.getUserProfile(user.uid);
      if (cachedUserData) {
        console.log('Using cached user data');
        setUserLeagues(cachedUserData.userLeagues);
        if (cachedUserData.currentLeague) {
          setCurrentLeague(cachedUserData.currentLeague);
        }
        setLoading(false);
        
        // Background refresh - update cache without blocking UI
        setTimeout(() => loadUserLeaguesFromFirebase(), 100);
        return;
      }
      
      // Load from Firebase if no cache
      await loadUserLeaguesFromFirebase();
    } catch (error) {
      console.error('Error loading user leagues:', error);
      // Reset state on error
      setCurrentLeague(null);
      setUserLeagues([]);
      setLeagueMembers([]);
      setIsLeagueAdmin(false);
      setLoading(false);
    }
  };

  const loadUserLeaguesFromFirebase = async () => {
    if (!user) return;
    
    try {
      // Get user document to find their leagues
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const leagues = userData.leagues || [];
        
        // Load league details for each league the user is in
        const leaguePromises = leagues.map(async (leagueId) => {
          // Check cache for individual leagues first
          const cachedLeague = cacheService.getLeagueInfo(leagueId);
          if (cachedLeague) {
            return cachedLeague;
          }
          
          const leagueDocRef = doc(firestore, 'leagues', leagueId);
          const leagueDoc = await getDoc(leagueDocRef);
          if (leagueDoc.exists()) {
            const leagueData = { id: leagueDoc.id, ...leagueDoc.data() };
            // Cache individual league data
            cacheService.setLeagueInfo(leagueId, leagueData);
            return leagueData;
          }
          return null;
        });

        const leagueData = await Promise.all(leaguePromises);
        const validLeagues = leagueData.filter(league => league !== null);
        
        setUserLeagues(validLeagues);
        
        // Set current league (use currentLeague from user doc, or first league)
        const currentLeagueId = userData.currentLeague || (validLeagues.length > 0 ? validLeagues[0].id : null);
        let currentLeagueData = null;
        if (currentLeagueId) {
          currentLeagueData = validLeagues.find(league => league.id === currentLeagueId);
          setCurrentLeague(currentLeagueData);
        }

        // Cache the complete user profile data
        const profileData = {
          userLeagues: validLeagues,
          currentLeague: currentLeagueData,
          userData: userData,
          lastUpdated: Date.now()
        };
        cacheService.setUserProfile(user.uid, profileData);

        // Preload data for all leagues in the background
        if (validLeagues.length > 0) {
          const currentYear = new Date().getFullYear();
          const seasonYear = new Date().getMonth() >= 6 ? currentYear : currentYear - 1;
          const currentSeason = `${seasonYear}-${seasonYear + 1}`;
          
          // Start preloading in background (don't await to avoid blocking UI)
          leagueDataPreloader.preloadAllLeagueData(user, validLeagues, currentSeason)
            .then(() => {
              console.log('🎯 League data preloading completed');
            })
            .catch(error => {
              console.warn('⚠️ League data preloading failed:', error);
            });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueMembers = async () => {
    if (!currentLeague || !user) return;

    try {
      const membersQuery = query(
        collection(firestore, 'leagues', currentLeague.id, 'members'),
        where('status', '==', 'active')
      );
      const membersSnapshot = await getDocs(membersQuery);
      const members = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setLeagueMembers(members);
    } catch (error) {
      console.error('Error loading league members:', error);
      setLeagueMembers([]);
    }
  };

  const checkAdminStatus = async () => {
    if (!currentLeague || !user) {
      setIsLeagueAdmin(false);
      return;
    }

    try {
      // Get user's username from their profile
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        setIsLeagueAdmin(false);
        return;
      }
      
      const userData = userDoc.data();
      const username = userData.username || user.displayName || user.email;
      
      // Check if username is in the league's admins array
      const isAdmin = currentLeague.admins && currentLeague.admins.includes(username);
      setIsLeagueAdmin(isAdmin);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsLeagueAdmin(false);
    }
  };

  const switchLeague = async (leagueId) => {
    if (!user) {
      console.log('No user available for league switch');
      return;
    }

    try {
      console.log(`🔄 Switching to league: ${leagueId}`);
      
      // Find and set the new current league immediately (for instant UI update)
      const newCurrentLeague = userLeagues.find(league => league.id === leagueId);
      if (newCurrentLeague) {
        // Check if we have preloaded data for instant switching
        const cachedLeagueInfo = cacheService.getLeagueInfo(leagueId);
        const currentSeason = (() => {
          const currentYear = new Date().getFullYear();
          const seasonYear = new Date().getMonth() >= 6 ? currentYear : currentYear - 1;
          return `${seasonYear}-${seasonYear + 1}`;
        })();
        
        if (cachedLeagueInfo) {
          console.log(`⚡ Using cached data for instant league switch`);
          // Use cached data for instant UI update
          setCurrentLeague(cachedLeagueInfo);
          setLeagueDataVersion(prev => prev + 1);
          
          // Load cached members if available
          const cachedMembers = cacheService.getLeagueMembers(leagueId);
          if (cachedMembers) {
            setLeagueMembers(cachedMembers);
          }
        } else {
          // Fallback to basic league info
          setCurrentLeague(newCurrentLeague);
        }

        // Update user's current league in Firestore (async, don't block UI)
        updateDoc(doc(firestore, 'users', user.uid), {
          currentLeague: leagueId
        }).catch(error => {
          console.warn('Failed to update current league in Firestore:', error);
        });

        // Background refresh: Get fresh data and update cache
        setTimeout(async () => {
          try {
            const leagueDocRef = doc(firestore, 'leagues', leagueId);
            const leagueDoc = await getDoc(leagueDocRef);
            
            if (leagueDoc.exists()) {
              const freshLeagueData = { 
                id: leagueDoc.id, 
                ...leagueDoc.data()
              };
              
              // Only update UI if data has changed
              const currentData = JSON.stringify(currentLeague);
              const freshData = JSON.stringify(freshLeagueData);
              
              if (currentData !== freshData) {
                setCurrentLeague(freshLeagueData);
                cacheService.setLeagueInfo(leagueId, freshLeagueData);
                setLeagueDataVersion(prev => prev + 1);
              }
              
              // Refresh members and admin status
              await Promise.all([
                loadLeagueMembers(),
                checkAdminStatus()
              ]);

              // Trigger background refresh of all league data
              leagueDataPreloader.backgroundRefreshLeague(user, freshLeagueData, currentSeason);
            }
          } catch (refreshError) {
            console.warn('Background refresh failed after league switch:', refreshError);
          }
        }, 50); // Small delay to not interfere with UI updates
      }
    } catch (error) {
      console.error('Error switching leagues:', error);
      // Don't throw the error, just log it to prevent crashes
    }
  };

  const refreshLeagueData = async () => {
    // Invalidate cache before refreshing
    if (user) {
      cacheService.removeUserProfile(user.uid);
    }
    if (currentLeague) {
      cacheService.remove(cacheService.getCacheKey('leagueInfo', currentLeague.id));
    }

    await loadUserLeagues();
    
    // Also refresh current league data if we have one
    if (currentLeague && user) {
      try {
        const leagueDocRef = doc(firestore, 'leagues', currentLeague.id);
        const leagueDoc = await getDoc(leagueDocRef);
        
        if (leagueDoc.exists()) {
          const updatedLeagueData = { 
            id: leagueDoc.id, 
            ...leagueDoc.data()
          };
          setCurrentLeague(updatedLeagueData);
          
          // Update cache with fresh data
          cacheService.setLeagueInfo(currentLeague.id, updatedLeagueData);
          
          // Increment version to trigger YearProvider refresh
          setLeagueDataVersion(prev => prev + 1);
          
          // Also refresh admin status and members
          await checkAdminStatus();
          await loadLeagueMembers();
        }
      } catch (error) {
        console.error('Error refreshing current league:', error);
      }
    }
  };

  const contextValue = {
    currentLeague,
    userLeagues,
    leagueMembers,
    isLeagueAdmin,
    loading,
    switchLeague,
    refreshLeagueData,
    leagueDataVersion
  };

  return (
    <LeagueContext.Provider value={contextValue}>
      {children}
    </LeagueContext.Provider>
  );
}